/**
 * WorkloadEngineeringReference — collapsible, fully-cited engineering + power
 * reference for the intent Workload step. Sits beneath the commercial
 * WorkloadAssumptionsTable and answers the technical half of "how do these
 * templates actually differ?":
 *
 *   1. Per-workload technical datapoints — rack density, the cooling technology
 *      that density forces, PUE, and the load / power-volatility profile.
 *   2. The reverse direction the user asked for — how each behind-the-meter
 *      (BTM) power source's capacity factor / lead time / dispatchability bounds
 *      the workload it can serve.
 *
 * EVERY number carries a superscript [n] into a numbered Sources footer, so the
 * "real referenced datapoints, not guesses" bar is visible in the UI. Pure
 * function of `workloadTechnicalReference.ts` — the single cited catalog shared
 * with DAM, SED, and the Feasibility / Power reports.
 */
import React from 'react';
import { WORKLOAD_PROFILES } from '../../lib/workloadProfile';
import {
  SOURCES,
  SOURCES_ORDERED,
  WORKLOAD_TECH_REFERENCE,
  BTM_SOURCE_REFERENCE,
  DATACENTER_POWER_MACRO,
  type SourceId,
} from '../../lib/workloadTechnicalReference';

/** Superscript citation marker — hover shows the source org / title / year. */
function Cite({ source }: { source: SourceId }): React.ReactElement {
  const s = SOURCES[source];
  return (
    <sup
      title={`${s.org} — ${s.title} (${s.year})`}
      className="ml-0.5 text-[9px] font-bold text-primary"
      data-testid={`cite-${source}`}
    >
      {s.n}
    </sup>
  );
}

const TH = 'type-meta whitespace-nowrap px-2 py-1.5 align-bottom font-bold text-text-muted';
const TD = 'px-2 py-2 text-[11px] text-text-secondary align-top';

export function WorkloadEngineeringReference(): React.ReactElement {
  return (
    <details
      className="mt-2 rounded-lg border border-border-subtle bg-state-hover/30"
      data-testid="workload-engineering-reference"
    >
      <summary className="cursor-pointer select-none px-3 py-2 type-meta font-semibold text-text-secondary hover:text-text-primary">
        Engineering &amp; power reference (rack density · cooling · PUE · BTM fit) — sourced
      </summary>

      <div className="px-3 pb-3">
        <p className="mb-3 text-[10px] italic leading-relaxed text-text-muted normal-case tracking-normal">
          {DATACENTER_POWER_MACRO.text}
          <Cite source={DATACENTER_POWER_MACRO.source} /> Every figure below is referenced — hover a
          number for the source, full list at the foot of the panel.
        </p>

        {/* ── Table 1 — per-workload technical datapoints ── */}
        <div className="type-meta mb-1 font-bold text-text-secondary">
          Technical profile by workload
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left" data-testid="workload-tech-grid">
            <thead>
              <tr className="border-b border-border-subtle">
                <th scope="col" className={TH}>Workload</th>
                <th scope="col" className={TH}>Rack density (kW/rack)</th>
                <th scope="col" className={TH}>Cooling required</th>
                <th scope="col" className={TH}>PUE</th>
                <th scope="col" className={TH}>Power profile</th>
                <th scope="col" className={TH}>BTM power fit</th>
              </tr>
            </thead>
            <tbody>
              {WORKLOAD_PROFILES.map((profile) => {
                const t = WORKLOAD_TECH_REFERENCE[profile.id];
                return (
                  <tr
                    key={profile.id}
                    data-testid={`workload-tech-row-${profile.id}`}
                    className="border-b border-border-subtle/60"
                  >
                    <th scope="row" className="whitespace-nowrap px-2 py-2 text-[11px] font-bold text-text-primary align-top">
                      {profile.label}
                    </th>
                    <td className={TD}>{t.rackDensityKw}<Cite source={t.rackDensitySource} /></td>
                    <td className={TD}>{t.coolingRequired}<Cite source={t.coolingSource} /></td>
                    <td className={`${TD} whitespace-nowrap`}>{t.typicalPue}<Cite source={t.pueSource} /></td>
                    <td className={TD}>{t.powerProfile}<Cite source={t.powerProfileSource} /></td>
                    <td className={TD}>{t.btmFit}<Cite source={t.btmFitSource} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* ── Table 2 — BTM power source ↔ workload (the reverse direction) ── */}
        <div className="type-meta mb-1 mt-4 font-bold text-text-secondary">
          BTM power source → workload fit
        </div>
        <p className="mb-1 text-[10px] italic leading-relaxed text-text-muted normal-case tracking-normal">
          The source you commit to bounds the workload you can serve: capacity factor sets how much
          overbuild + storage a high-load-factor AI cluster needs, and lead time sets whether it can
          match a fast AI build-out.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left" data-testid="btm-source-grid">
            <thead>
              <tr className="border-b border-border-subtle">
                <th scope="col" className={TH}>BTM source</th>
                <th scope="col" className={TH}>Capacity factor</th>
                <th scope="col" className={TH}>Indicative LCOE</th>
                <th scope="col" className={TH}>Lead time</th>
                <th scope="col" className={TH}>Dispatchability</th>
                <th scope="col" className={TH}>Best-fit workloads</th>
              </tr>
            </thead>
            <tbody>
              {BTM_SOURCE_REFERENCE.map((b) => (
                <tr
                  key={b.id}
                  data-testid={`btm-source-row-${b.id}`}
                  className="border-b border-border-subtle/60"
                >
                  <th scope="row" className="px-2 py-2 text-[11px] font-bold text-text-primary align-top">
                    {b.label}
                  </th>
                  <td className={`${TD} whitespace-nowrap`}>{b.capacityFactor}<Cite source={b.capacityFactorSource} /></td>
                  <td className={`${TD} whitespace-nowrap`}>
                    {b.lcoe}
                    {b.lcoeSource && <Cite source={b.lcoeSource} />}
                  </td>
                  <td className={TD}>{b.leadTime}</td>
                  <td className={TD}>{b.dispatchable}</td>
                  <td className={TD}>{b.bestFitWorkloads}<Cite source={b.noteSource} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ── Sources footer ── */}
        <div className="type-meta mb-1 mt-4 font-bold text-text-secondary">
          Sources
        </div>
        <ol className="space-y-0.5" data-testid="workload-reference-sources">
          {SOURCES_ORDERED.map((s) => (
            <li key={s.n} className="text-[10px] leading-relaxed text-text-muted normal-case tracking-normal">
              <span className="font-bold text-text-secondary">{s.n}.</span>{' '}
              <a
                href={s.url}
                target="_blank"
                rel="noreferrer"
                className="text-primary underline decoration-dotted underline-offset-2 hover:text-text-primary"
              >
                {s.org} — {s.title} ({s.year})
              </a>
              <span className="block pl-4 italic text-text-muted/80">{s.backs}</span>
            </li>
          ))}
        </ol>

        <p className="mt-2 text-[10px] italic leading-relaxed text-text-muted normal-case tracking-normal">
          Reference figures for choosing a template and power strategy — they do not feed a site's
          LCOE or scoring (those come from its Financial Assumptions and power-stack resolver).
        </p>
      </div>
    </details>
  );
}
